import localforage from "localforage";
import { atom } from "recoil";

export type PreviousIncidentDetails = {
  date: {
    key: string;
    value: Date;
  };
  "incident-number": {
    key: string;
    value: number;
  };
  "patient-age": {
    key: string;
    value: number;
  };
  "patient-sex": {
    key: string;
    value: string;
  };
};

export const previousIncidentDetailsAtom = atom<PreviousIncidentDetails | null>({
  key: "previousIncidentDetailsAtom",
  default: null,
  effects: [
    ({ onSet, setSelf, trigger }) => {
      const loadState = async () => {
        try {
          const data = await localforage.getItem<PreviousIncidentDetails>("previousIncidentDetails");
          data ? setSelf(data) : setSelf(null);
        } catch (error) {
          console.log(error);
          setSelf(null);
        }
      };

      if (trigger === "get") {
        loadState();
      }

      onSet(async (newValue, _oldValue, isReset) => {
        if (isReset) {
          setSelf(null);
          await localforage.removeItem("previousIncidentDetails");
        } else if (newValue !== undefined) {
          await localforage.setItem("previousIncidentDetails", newValue);
        }
      });
    },
  ],
});

/** One supervisor row from role; `key` ties email + name fields together for autofill sync. */
export type SupervisorEmailValue = {
  key: string;
  email: string;
  name: string;
};

export const supervisorEmailValueKey = (email: string): string => email.trim().toLowerCase();

/** Normalizes API rows (e.g. `{ email, name }`) into values with a stable `key`. */
export function normalizeSupervisorEmailRows(rows: unknown): SupervisorEmailValue[] {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows
    .map((row: unknown): SupervisorEmailValue | null => {
      if (row === null || typeof row !== "object") {
        return null;
      }
      const o = row as Record<string, unknown>;
      const email = String(o.email ?? o.Email ?? "").trim();
      const name = String(o.name ?? o.Name ?? "").trim();
      if (!email) {
        return null;
      }
      return {
        key: supervisorEmailValueKey(email),
        email,
        name,
      };
    })
    .filter((r): r is SupervisorEmailValue => r !== null);
}

export function findSupervisorEmailValueByInput(
  rows: SupervisorEmailValue[] | null | undefined,
  input: string
): SupervisorEmailValue | null {
  const t = input.trim();
  if (!rows?.length || !t) {
    return null;
  }
  const tl = t.toLowerCase();
  const byEmail = rows.find((r) => r.key === tl || r.email.toLowerCase() === tl);
  if (byEmail) {
    return byEmail;
  }
  const byName = rows.find((r) => {
    const n = r.name.trim();
    return n === t || n.toLowerCase() === tl;
  });
  return byName ?? null;
}

export const supervisorEmailsFromRoleAtom = atom<SupervisorEmailValue[] | null>({
  key: "supervisorEmailsFromRoleAtom",
  default: [],
  effects: [
    ({ onSet, setSelf }) => {
      setSelf([]);

      onSet((newValue, _oldValue, isReset) => {
        if (isReset) {
          return [];
        } else if (newValue !== undefined) {
          return newValue;
        }
      });
    },
  ],
});

/** When set to a row, supervisor autofill Email + Text fields sync via `SupervisorEmailValue.key`. */
export const supervisorAutofillSelectionAtom = atom<SupervisorEmailValue | null>({
  key: "supervisorAutofillSelectionAtom",
  default: null,
});
